import { Form, Formik, type FormikHelpers } from 'formik'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { Announced } from '@dhl/illustrations'
import { useDownload } from '@dhl/axios'
import { Box, Button, Grid, H5 } from '@dhl-ui/components'

import { TrackerCodeSearchField, useNotificationMessage } from '~/common'

type FormValues = {
  trackerCode: string
}

export const ReprintPage = () => {
  const { t } = useTranslation()
  const { triggerDownload } = useDownload()

  const { showError } = useNotificationMessage()

  const initialValues: FormValues = {
    trackerCode: '',
  }

  const createValidationSchema = (t: TFunction) =>
    Yup.object({
      trackerCode: Yup.string().required(t('admin.reprintPage.inputTrackerCodeMessage')).default(''),
    }).defined()

  const onSubmit = async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
    const trackerCode = values.trackerCode.toUpperCase()
    try {
      await downloadFile(trackerCode)
    } catch {
      helpers.setFieldError('trackerCode', `${t('admin.reprintPage.helperText')} ${trackerCode}`)
      helpers.setSubmitting(false)
    }
  }

  const downloadFile = async (trackerCode: string) => {
    try {
      await triggerDownload(`/api/admin-reprint-labels?trackerCode=${trackerCode}`, `${trackerCode}.pdf`)
    } catch (error: any) {
      if (error.response && error.response.data) {
        const errorResponse = JSON.parse(await error.response.data.text())
        showError(errorResponse.message)
      } else {
        showError(t('admin.reprintPage.error'))
      }
    }
  }

  const validationSchema = createValidationSchema(t)

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
      {({ isSubmitting }) => (
        <Form data-testid="reprint-form">
          <Box pt={2} pb={4}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 4 }}>
                <TrackerCodeSearchField />
              </Grid>
              <Grid size={{ xs: 3 }}>
                <Button
                  data-testid="submit-reprint"
                  disabled={isSubmitting}
                  color="primary"
                  type="submit"
                  variant="contained"
                >
                  {t('admin.reprintPage.trackerCodeDownloadButton')}
                </Button>
              </Grid>
            </Grid>
            <Grid>
              <Box
                pt={1}
                pb={2}
                sx={{ backgroundColor: 'common.white', marginTop: 3 }}
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Announced sx={{ width: '255px', height: '169px' }} />
              </Box>
            </Grid>
            <Grid>
              <Box
                pt={1}
                pb={10}
                sx={{ backgroundColor: 'common.white' }}
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <H5>{t('admin.reprintPage.perfectTrackerCodeTipsText')}</H5>
              </Box>
            </Grid>
          </Box>
        </Form>
      )}
    </Formik>
  )
}
